import { useEffect } from "react";
import { PLAYER_COLORS, PLAYER_ON_COLOR, serverNow } from "@/lib/api";
import { FaceChip } from "@/components/game/WeatherDice";
import { weatherCardSrc } from "@/lib/artAssets";
import { globalEffectText } from "@/components/game/WeatherCardReveal";
import Coin from "@/components/game/Coin";
import { useStageLayout } from "@/components/game/Stage";
import { CountdownBar, CoverBody, DecidingBody, useAutoDecline, useResponseView, useServerCountdown } from "@/components/game/ResponseWindow";

// WEATHER v3 sequence (runs by itself, LAYOUT F): the server draws when the last turn ends →
// the weather card + global card overlay (WeatherOverlay, every screen, server time) → the area reveal on the board →
// here, in the SELECTED CARD area: results table → Protection prompts → "Next round in 5…".

function Part({ part, focus, children }) {
  return (
    <div data-focus-part={part} className="focus-region" style={{ opacity: focus && part !== focus ? 0.5 : 1 }}>
      {children}
    </div>
  );
}

const STAT_LABEL = { absorb: "Absorption", shade: "Shading", storm: "Storm protection" };

export function areaText(ws, faces) {
  if (!ws) return "";
  const cap = faces?.surplus_pay_cap ?? 2;
  if (ws.kind === "none") return "Nothing happens this round.";
  const surge = faces?.surge || { extra_level: 1, cost_per_square: 2 };
  if (ws.kind === "surge")
    return `The sea rises ${surge.extra_level} level above its permanent height for this round only. Each of your squares it reaches costs ${surge.cost_per_square} repairs minus that square's storm protection. Walls and higher ground hold it back; nothing is destroyed. Spare storm protection earns nothing.`;
  const n = ws.shape_size || (ws.area || []).length;
  return `A shape of ${n} connected squares: ${ws.need} per square of yours in it. Tests ${STAT_LABEL[ws.stat] || "Absorption"} in and around it. Spare ${(STAT_LABEL[ws.stat] || "Absorption").toLowerCase()} earns up to ${cap}.`;
}

const COLS = { gridTemplateColumns: "minmax(0,1.3fr) minmax(0,0.8fr) minmax(0,1fr) minmax(0,1.05fr)", columnGap: 4 };

// Repairs / Earned cell: "−2" repairs (red), "+2" earned (green), "protected", or "0".
function Outcome({ r, color }) {
  if (r.protected) return <span className="font-bold text-[color:var(--sea)]" style={{ fontSize: 10.5 }}>protected</span>;
  const repairs = r.repairs ?? 0;
  const earned = r.earned ?? 0;
  const kind = repairs > 0 ? "repairs" : earned > 0 ? "earned" : "none";
  const fg = kind === "repairs" ? "var(--danger)" : kind === "earned" ? "#2F7A4A" : "var(--ink)";
  return (
    <span data-testid={`weather-result-outcome-${color}`} data-kind={kind} className="inline-flex items-center gap-1 font-bold tabular-nums" style={{ color: fg }} title={kind === "repairs" ? `Repairs ${repairs}` : kind === "earned" ? `You earn ${earned}` : "No repairs"}>
      <Coin size={12} standalone />
      {kind === "repairs" ? `−${repairs}` : kind === "earned" ? `+${earned}` : "0"}
    </span>
  );
}

function ResultsTable({ game, ws }) {
  // Tablet canvas: the panel is ~220px inside, so the headers are abbreviated (full names in the tooltips).
  const { compact } = useStageLayout();
  return (
    <div data-testid="weather-results" className="rounded-[8px] border border-[color:var(--line)] bg-[color:var(--card)] overflow-hidden">
      <div className="grid items-center px-2 py-1 bg-[color:var(--line-soft)]" style={{ ...COLS, fontSize: 9, lineHeight: "12px", fontWeight: 700, textTransform: "uppercase", color: "var(--ink-3)" }}>
        <span>Player</span>
        <span className="text-center" title={`${STAT_LABEL[ws.stat] || "Absorption"} needed`}>Needed</span>
        <span className="text-center" title={`${STAT_LABEL[ws.stat] || "Absorption"} you have in and around the area`}>{compact ? "Have" : "You have"}</span>
        <span className="text-right" title="Repairs (red) or money earned (green)">{compact ? "Rep/Earn" : "Repairs / Earned"}</span>
      </div>
      {game.players.map((p, i) => {
        const r = ws.results?.[p.color] || {};
        const hit = !!r.hit;
        return (
          <div
            key={p.color}
            data-testid={`weather-result-${p.color}`}
            className="grid items-center px-2 border-t border-[color:var(--line-soft)]"
            style={{ ...COLS, minHeight: 28, fontSize: 12.5 }}
          >
            <span className="flex items-center gap-1.5 min-w-0" title={p.name}>
              <span className="shrink-0 inline-flex items-center justify-center" style={{ width: 16, height: 16, borderRadius: 4, background: PLAYER_COLORS[p.color], color: PLAYER_ON_COLOR[p.color], fontSize: 10, fontWeight: 700 }}>
                {i + 1}
              </span>
              <span className="font-semibold text-[color:var(--ink)] truncate">{p.name}</span>
            </span>
            {!hit ? (
              <span className="text-right text-[color:var(--ink-3)]" style={{ gridColumn: "2 / -1", fontSize: 11.5 }} data-testid={`weather-result-status-${p.color}`}>
                not in the area
              </span>
            ) : (
              <>
                <span className="text-center tabular-nums font-semibold" data-testid={`weather-result-needed-${p.color}`}>{r.demand}</span>
                <span className="text-center tabular-nums" data-testid={`weather-result-have-${p.color}`} title={r.group_bonus > 0 ? `includes +${r.group_bonus} from nature groups` : undefined}>
                  <span className="font-semibold">{r.supply}</span>
                  {r.group_bonus > 0 && (
                    <span className="block text-[9px] leading-tight text-[color:var(--ink-3)]" data-testid={`weather-result-bonus-${p.color}`}>
                      +{r.group_bonus} from nature groups
                    </span>
                  )}
                </span>
                <span className="text-right" data-testid={`weather-result-repairs-${p.color}`}>
                  <Outcome r={r} color={p.color} />
                </span>
                {r.text && (
                  <span data-testid={`weather-result-text-${p.color}`} className="text-[color:var(--ink-2)]" style={{ gridColumn: "1 / -1", fontSize: 11, lineHeight: "14px", paddingBottom: 4 }}>
                    {r.text}
                  </span>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Protection response window: one private panel per player with repairs > 0, the same size
// whether or not they hold a Protection Card. Everyone else sees only "Players are deciding…".
const RESPONSE_H = 176;

// PAY UPFRONT: the Protection Card was paid when taken, so playing it costs nothing more.
function ProtectionWindow({ game, ws, onDecide, busy, focus, responder }) {
  const win = {
    responders: ws.responders,
    answers: ws.answers,
    options: ws.options,
    current: ws.target_color,
    deadline: ws.protection_deadline,
  };
  const view = useResponseView(win, responder);
  const { remaining, total, deadlineMs } = useServerCountdown(ws.protection_deadline);
  useAutoDecline({ enabled: !responder.online && view.mode !== "deciding", remaining, deadlineMs, busy, onDecide });
  const timeUp = !!deadlineMs && remaining <= 0;
  const player = game.players.find((p) => p.color === view.color);
  const r = (view.color && ws.results?.[view.color]) || {};
  const repairs = r.repairs ?? 0;

  let body;
  if (view.mode === "deciding") {
    body = <DecidingBody remaining={remaining} />;
  } else if (view.mode === "cover") {
    body = <CoverBody name={player?.name || "Player"} onReveal={view.reveal} />;
  } else {
    body = (
      <>
        {r.text && (
          <div className="mt-2" data-testid="protection-result-text" style={{ fontSize: 12, lineHeight: "16px", color: "var(--ink-2)" }}>
            {ws.face_name}: {r.text}
          </div>
        )}
        <div className="mt-1.5" data-testid="protection-question" style={{ fontSize: 14, lineHeight: "18px", fontWeight: 700, color: "var(--ink)" }}>
          {view.option ? `Play Protection Card to cancel your repairs of ${repairs}?` : `Repairs ${repairs}. You have no response.`}
        </div>
        <div className="mt-auto pt-2 flex" style={{ gap: 6 }}>
          {view.option ? (
            <>
              <button data-testid="protection-play" onClick={() => onDecide(true)} disabled={busy || timeUp} className="btn-primary btn-compact btn-card flex-1">
                Yes, play it
              </button>
              <button data-testid="protection-decline" onClick={() => onDecide(false)} disabled={busy} className="btn-secondary btn-compact btn-card flex-1">
                No, pay {repairs}
              </button>
            </>
          ) : (
            <button data-testid="protection-continue" onClick={() => onDecide(false)} disabled={busy} className="btn-primary btn-compact btn-card flex-1">
              Continue
            </button>
          )}
        </div>
      </>
    );
  }

  return (
    <Part focus={focus} part="response">
      <div data-testid="protection-prompt" data-mode={view.mode} className="rounded-[8px] border-2 border-[color:var(--sea)] bg-[color:var(--surface)] p-3 flex flex-col" style={{ height: RESPONSE_H }}>
        <div className="flex items-center justify-between gap-2 shrink-0" style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-2)" }}>
          <span className="truncate" data-testid="protection-prompt-head">{view.mode === "mine" ? (responder.online ? "Your response" : `${player?.name}: your response`) : "Responses"}</span>
          <span data-testid="protection-countdown" className="shrink-0 tabular-nums">{timeUp ? "Time is up" : `${remaining}s`}</span>
        </div>
        <div className="mt-1 shrink-0">
          <CountdownBar remaining={remaining} total={total} testid="protection-countdown-bar" />
        </div>
        {body}
      </div>
    </Part>
  );
}

const KICKER = { fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--ink-4)" };
const TITLE = { fontSize: 16, lineHeight: "20px", fontWeight: 700, color: "var(--ink)" };
const SUB = { fontSize: 12, lineHeight: "16px", color: "var(--ink-3)" };

// AUTO FINISH: "Next round in 5…" on the server clock; at 0 the screen asks the server
// (any request after the deadline ends the round, once).
function FinishCountdown({ ws, onDeadline }) {
  const { remaining, total, deadlineMs } = useServerCountdown(ws.finish_deadline);
  useEffect(() => {
    if (!deadlineMs || !onDeadline) return undefined;
    const left = Math.max(0, deadlineMs - serverNow());
    const t1 = setTimeout(onDeadline, left + 250);
    const t2 = setTimeout(onDeadline, left + 2500); // a second try if the first raced the deadline
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [deadlineMs]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!deadlineMs) return null;
  return (
    <div data-testid="weather-next-round" className="flex flex-col" style={{ gap: 4 }}>
      <div className="flex items-center justify-between" style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink-2)" }}>
        <span data-testid="weather-next-round-text">{remaining > 0 ? `Next round in ${remaining}…` : "Next round…"}</span>
      </div>
      <CountdownBar remaining={remaining} total={total} testid="weather-next-round-bar" />
    </div>
  );
}

// WEATHER RUNS BY ITSELF — the results part, in the SELECTED CARD area of the left column.
// timeline: useWeatherTimeline (the overlay + board reveal come first; the results after).
// responder: { online, seat } — whose private response panel this screen may show (ResponseWindow).
export default function WeatherFlow({ game, faces, timeline = null, onProtectionDecide, onDeadline, busy, focus = null, responder = { online: false, seat: null } }) {
  const ws = game.weather_state;
  if (game.phase !== "weather") return null;
  const step = ws?.step;
  const shown = !!ws && (!timeline || timeline.resultsShown);
  let body;
  if (!shown) {
    body = (
      <Part focus={focus} part="header">
        <div style={KICKER}>End of round {game.round}</div>
        <div style={TITLE} className="mt-0.5" data-testid="weather-spinning-title">The weather card</div>
        <div style={SUB} className="mt-0.5" data-testid="weather-sweeping">{timeline?.phase === "reveal" ? "Finding where it hits…" : "Drawing the weather…"}</div>
      </Part>
    );
  } else {
    const hitNames = game.players.filter((p) => ws.results?.[p.color]?.hit).map((p) => p.name);
    const g = ws.global_card;
    body = (
      <>
        <Part focus={focus} part="wheel">
          <div className="flex items-center" style={{ gap: 12 }} data-testid="weather-result-head" data-face={ws.face}>
            {weatherCardSrc(ws.face) ? (
              <img decoding="async" src={weatherCardSrc(ws.face)} alt={ws.face_name} draggable={false} data-testid="weather-result-card" style={{ width: 38, aspectRatio: "900 / 1440", borderRadius: 4, objectFit: "cover", boxShadow: "0 1px 4px rgba(36,40,38,.3)" }} />
            ) : (
              <FaceChip faceKey={ws.face} size={48} radius={10} reveal />
            )}
            <div className="min-w-0">
              <div style={KICKER}>{step === "done" ? "Weather resolved" : `End of round ${game.round}`}</div>
              <div className="mt-0.5 flex items-center" style={{ gap: 6 }}>
                <FaceChip faceKey={ws.face} size={20} radius={5} testid="weather-result-chip" />
                <span data-testid="weather-die-name" style={TITLE} className="truncate">{ws.face_name}</span>
              </div>
            </div>
          </div>
        </Part>
        {ws.kind === "none" ? (
          <div data-testid="weather-area-text" style={{ ...SUB, fontWeight: 600, color: "var(--ink-2)" }}>Clear Skies, nothing happens</div>
        ) : (
          <>
            <Part focus={focus} part="header">
              <div data-testid="weather-area-text" style={SUB}>{areaText(ws, faces)}</div>
            </Part>
            <ResultsTable game={game} ws={ws} />
            {hitNames.length === 0 && (
              <div className="text-center" style={SUB} data-testid="weather-nobody-hit">
                Nobody has a square in the area.
              </div>
            )}
          </>
        )}
        {g?.no && (
          <div data-testid="weather-global-line" className="rounded-[6px] border border-[color:var(--line)] px-2 py-1" style={{ fontSize: 11.5, lineHeight: "15px", color: "var(--ink-2)" }}>
            <b style={{ color: "var(--ink)" }}>Global card: {g.name}</b>{" "}
            <span style={{ color: g.effect === "warming" ? "#B5532E" : "var(--sea)", fontWeight: 600 }}>{globalEffectText(g)}</span>
            <span style={{ color: "var(--ink-3)" }}> · counts after the weather</span>
          </div>
        )}
        {(ws.block_notices || []).length > 0 && (
          <div data-testid="weather-block-notices" className="flex flex-col gap-1">
            {ws.block_notices.map((n, i) => (
              <div key={i} className="rounded-[6px] border border-[color:var(--sea)] px-2 py-1" style={{ fontSize: 11.5, fontWeight: 600, color: "var(--sea)" }}>
                {n}
              </div>
            ))}
          </div>
        )}
        {step === "protection_prompt" && (
          <ProtectionWindow game={game} ws={ws} onDecide={onProtectionDecide} busy={busy} focus={focus} responder={responder} />
        )}
        {step === "done" && <FinishCountdown ws={ws} onDeadline={onDeadline} />}
      </>
    );
  }

  return (
    <div data-testid="weather-flow" data-step={step || "spin"} data-phase={timeline?.phase || "results"}>
      <div data-testid="weather-panel" className="animate-in fade-in duration-200 flex flex-col" style={{ gap: 10 }}>
        {body}
      </div>
    </div>
  );
}
